// Levels in Mole town. The id is fixed (progress is saved under it) – never change it once a level is published.
import { defineLevels } from '../../core/registry';
import type { LevelData, StaticData } from '../../core/types';
import { on } from '../../core/level-helpers';
import { DEG, P } from './shared';

// Static blocks: dirt shelf, rock
const J = (x: number, y: number, w: number, h: number, a?: number): StaticData => ({ x, y, w, h, a: a || 0, skin: 'moles_dirt' });
const KL = (x: number, y: number, w: number, h: number, a?: number): StaticData => ({ x, y, w, h, a: a || 0, skin: 'moles_rock' });

// Shelf that starts right at the lift's door (the lift stands at its top/bottom at topY)
function exitLedge(lx: number, topY: number, ex: number, w: number, a?: number, h?: number): StaticData {
  h = h || 26; a = a || 0;
  const edge = lx + ex * 62, r = a * DEG;
  return J(Math.round(edge + ex * (w / 2) * Math.cos(r)), Math.round(topY + 6 + h / 2 + (w / 2) * Math.sin(r)), w, h, ex * a);
}


const L: LevelData[] = [];
{ // 1 – Up in the lift – intro: hoist (put the sack in the bucket → the lift carries the ball up)
  L.push({
    id: 'up-the-lift', par: 1, tools: { moles_sack: 1 }, learn: ['pulley', 'weight'], intro: 'moles_hoist',
    statics: [J(565, 424, 420, 26, 4), J(870, 615, 320, 30), J(300, 770, 200, 40), J(140, 690, 160, 30)],
    balls: [{ x: 320, y: 668, r: P }],
    goal: { type: 'bowl', x: 870, y: 600, w: 180 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 272, y: 110 }, { x: 178, y: 110 }], A: { type: 'lift', x: 300, y: 690, exit: 1 }, B: { type: 'bucket', x: 150, y: 330 }, max: 300 },
      { t: 'duck', x: 620, y: 370 },
    ],
    solution: [{ t: 'moles_sack', x: 150, y: 200 }],
  });
}
{ // 2 – The mine cart – intro: mine cart (build a rail to the cart)
  const s1 = J(190, 300, 330, 22, 4);
  L.push({
    id: 'mine-cart', par: 1, tools: { plank: 1 }, learn: ['ramp', 'mechanism'], intro: 'moles_cart',
    statics: [s1, J(450, 775, 300, 30), J(1010, 715, 260, 30)],
    balls: [on(s1, -120, P)],
    goal: { type: 'bowl', x: 1010, y: 700, w: 180 },
    objs: [{ t: 'moles_cart', x: 590, y: 440, tx: 860, ty: 520, tip: true }, { t: 'duck', x: 760, y: 455 }],
    solution: [{ t: 'plank', x: 445, y: 340, a: 15 }],
  });
}
{ // 3 – The rope pulls – the sack in the bucket pulls the cart along the track
  L.push({
    id: 'rope-pulls', par: 1, tools: { moles_sack: 1 }, learn: ['pulley', 'weight'],
    statics: [J(440, 460, 340, 24), J(690, 655, 210, 30)],
    balls: [{ x: 310, y: 397, r: P }],
    goal: { type: 'bowl', x: 690, y: 640, w: 170 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 850, y: 432 }, { x: 960, y: 432 }], A: { type: 'cart', x: 330, y: 420, tx: 550, ty: 420, tip: true }, B: { type: 'bucket', x: 988, y: 560 }, max: 220, fric: 0.12 },
      { t: 'duck', x: 560, y: 240 },
    ],
    solution: [{ t: 'moles_sack', x: 990, y: 300 }],
  });
}
{ // 4 – Heavy or light – new: leaf (too light); only the heavy sack can raise the lift
  L.push({
    id: 'heavy-or-light', par: 1, tools: { moles_leaf: 1, moles_sack: 1 }, learn: ['weight', 'pulley'],
    statics: [exitLedge(880, 400, -1, 360, 4), J(300, 625, 300, 30), J(880, 775, 200, 40)],
    balls: [{ x: 860, y: 678, r: P }],
    goal: { type: 'bowl', x: 300, y: 610, w: 180 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 908, y: 110 }, { x: 1012, y: 110 }], A: { type: 'lift', x: 880, y: 700, exit: -1 }, B: { type: 'bucket', x: 1040, y: 330 }, max: 300 },
      { t: 'moles_mole', x: 600, y: 588 },
      { t: 'duck', x: 560, y: 300 },
    ],
    solution: [{ t: 'moles_sack', x: 1040, y: 190 }],
    fails: [[{ t: 'moles_leaf', x: 1040, y: 190 }]],
  });
}
{ // 5 – Into the deep – put the sack in the lift so it is the heavier side and goes down
  L.push({
    id: 'into-the-deep', par: 1, tools: { moles_leaf: 1, moles_sack: 1 }, learn: ['weight', 'pulley'],
    statics: [exitLedge(300, 590, 1, 300, 4), J(800, 755, 260, 30), J(150, 740, 200, 30), J(560, 345, 300, 26)],
    balls: [{ x: 330, y: 308, r: P }],
    goal: { type: 'bowl', x: 800, y: 740, w: 180 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 272, y: 100 }, { x: 178, y: 100 }], A: { type: 'lift', x: 300, y: 330, exit: 1 }, B: { type: 'sack', x: 150, y: 650, m: 16 }, min: -260, max: 0 },
      { t: 'duck', x: 560, y: 560 },
    ],
    solution: [{ t: 'moles_sack', x: 270, y: 190 }],
    fails: [[{ t: 'moles_leaf', x: 270, y: 190 }]],
  });
}
{ // 6 – Two crystals – new: crystal; one is too light, two are enough (counting)
  L.push({
    id: 'two-crystals', par: 2, tools: { moles_crystal: 2, moles_leaf: 1 }, learn: ['weight', 'counting', 'pulley'],
    statics: [exitLedge(300, 380, 1, 380, 5), J(900, 600, 300, 30), J(300, 770, 200, 40)],
    balls: [{ x: 320, y: 668, r: P }],
    goal: { type: 'bowl', x: 900, y: 585, w: 180 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 272, y: 100 }, { x: 178, y: 100 }], A: { type: 'lift', x: 300, y: 690, exit: 1 }, B: { type: 'bucket', x: 150, y: 320 }, max: 310 },
      { t: 'duck', x: 900, y: 300 },
    ],
    solution: [{ t: 'moles_crystal', x: 160, y: 240 }, { t: 'moles_crystal', x: 130, y: 170 }],
    fails: [[{ t: 'moles_crystal', x: 150, y: 200 }], [{ t: 'moles_crystal', x: 160, y: 240 }, { t: 'moles_leaf', x: 130, y: 170 }]],
  });
}
{ // 7 – Uphill – the cart must be pulled uphill; the crystal is too light (decoys: crystal, leaf)
  L.push({
    id: 'uphill', par: 1, tools: { moles_crystal: 1, moles_sack: 1, moles_leaf: 1 }, learn: ['weight', 'pulley', 'ramp'],
    statics: [J(660, 715, 220, 30)],
    balls: [{ x: 305, y: 540, r: P }],
    goal: { type: 'bowl', x: 660, y: 700, w: 170 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 800, y: 300 }], A: { type: 'cart', x: 320, y: 560, tx: 540, ty: 440, tip: true, m: 12 }, B: { type: 'bucket', x: 828, y: 470 }, max: 250, fric: 0.04 },
      { t: 'duck', x: 450, y: 420 },
    ],
    solution: [{ t: 'moles_sack', x: 840, y: 380 }],
    fails: [[{ t: 'moles_crystal', x: 840, y: 380 }], [{ t: 'moles_leaf', x: 840, y: 380 }]],
  });
}
{ // 8 – Follow the rope – which bucket belongs to the lift? (decoy: the bucket for the gate)
  L.push({
    id: 'follow-the-rope', par: 1, tools: { moles_sack: 1, moles_leaf: 1 }, learn: ['pulley', 'mechanism'],
    statics: [exitLedge(420, 400, 1, 260, 5), J(820, 655, 220, 30), J(420, 775, 200, 40), J(110, 690, 180, 30)],
    balls: [{ x: 440, y: 678, r: P }],
    goal: { type: 'bowl', x: 820, y: 640, w: 170 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 448, y: 90 }, { x: 1022, y: 90 }], A: { type: 'lift', x: 420, y: 700, exit: 1 }, B: { type: 'bucket', x: 1050, y: 300 }, max: 300 },
      { t: 'moles_hoist', wheels: [{ x: 222, y: 190 }, { x: 138, y: 190 }], A: { type: 'gate', x: 110, y: 595, h: 150 }, B: { type: 'bucket', x: 250, y: 360 }, max: 160 },
      { t: 'duck', x: 1050, y: 560 },
    ],
    solution: [{ t: 'moles_sack', x: 1050, y: 190 }],
    fails: [[{ t: 'moles_sack', x: 250, y: 230 }]],
  });
}
{ // 9 – The hatch – raise the hatch with a heavy enough weight before the ball reaches it
  const s1 = J(420, 380, 700, 24, 6);
  L.push({
    id: 'hatch', par: 1, tools: { moles_leaf: 1, moles_crystal: 1, moles_sack: 1 }, learn: ['weight', 'pulley', 'mechanism'],
    statics: [s1, J(900, 625, 260, 30)],
    balls: [on(s1, -300, P)],
    goal: { type: 'bowl', x: 900, y: 610, w: 180 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 728, y: 100 }, { x: 1022, y: 100 }], A: { type: 'gate', x: 700, y: 336, h: 130 }, B: { type: 'bucket', x: 1050, y: 300 }, max: 140 },
      { t: 'duck', x: 900, y: 420 },
    ],
    solution: [{ t: 'moles_sack', x: 1050, y: 190 }],
    fails: [[{ t: 'moles_crystal', x: 1050, y: 190 }], [{ t: 'moles_leaf', x: 1050, y: 190 }]],
  });
}
{ // 10 – Up or down – the heavy side goes down (sack in the bucket, not in the lift)
  L.push({
    id: 'up-or-down', par: 1, tools: { moles_sack: 1, moles_leaf: 1 }, learn: ['weight', 'pulley', 'direction'],
    statics: [exitLedge(300, 250, 1, 420, 5), J(900, 485, 240, 30), exitLedge(300, 630, 1, 900, 3)],
    balls: [{ x: 330, y: 408, r: P }],
    goal: { type: 'bowl', x: 900, y: 470, w: 170 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 272, y: 70 }, { x: 178, y: 70 }], A: { type: 'lift', x: 300, y: 430, exit: 1 }, B: { type: 'bucket', x: 150, y: 430, m: 9.4 }, min: -200, max: 180, fric: 0.02 },
      { t: 'moles_mole', x: 640, y: 628, look: -1 },
      { t: 'duck', x: 600, y: 200 },
    ],
    solution: [{ t: 'moles_sack', x: 150, y: 300 }],
    fails: [[{ t: 'moles_sack', x: 280, y: 280 }], [{ t: 'moles_leaf', x: 150, y: 300 }]],
  });
}
{ // 11 – Coal on the lift – the lift is heavy, it takes two sacks
  L.push({
    id: 'coal-on-the-lift', par: 2, tools: { moles_sack: 2, moles_crystal: 1, moles_leaf: 1 }, learn: ['weight', 'counting', 'pulley'],
    statics: [exitLedge(260, 390, 1, 420, 4), J(920, 625, 280, 30), J(260, 775, 200, 40)],
    balls: [{ x: 280, y: 678, r: P }],
    goal: { type: 'bowl', x: 920, y: 610, w: 180 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 232, y: 90 }, { x: 128, y: 90 }], A: { type: 'lift', x: 260, y: 700, exit: 1, m: 20, coal: true }, B: { type: 'bucket', x: 100, y: 300 }, max: 310 },
      { t: 'duck', x: 620, y: 330 },
    ],
    solution: [{ t: 'moles_sack', x: 75, y: 200 }, { t: 'moles_sack', x: 125, y: 200 }],
    fails: [[{ t: 'moles_sack', x: 100, y: 190 }], [{ t: 'moles_sack', x: 75, y: 200 }, { t: 'moles_crystal', x: 125, y: 200 }]],
  });
}
{ // 12 – Two floors – two lifts one after the other (the upper one has a brake)
  L.push({
    id: 'two-floors', par: 2, tools: { moles_sack: 2, moles_leaf: 1 }, learn: ['pulley', 'weight', 'chain_reaction'],
    statics: [exitLedge(230, 500, 1, 200, 5), exitLedge(560, 240, 1, 120, 5), J(900, 455, 220, 30), J(230, 790, 200, 30)],
    balls: [{ x: 250, y: 698, r: P }],
    goal: { type: 'bowl', x: 900, y: 440, w: 170 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 202, y: 80 }, { x: 108, y: 80 }], A: { type: 'lift', x: 230, y: 720, exit: 1 }, B: { type: 'bucket', x: 80, y: 300 }, max: 220 },
      { t: 'moles_hoist', wheels: [{ x: 588, y: 70 }, { x: 1062, y: 70 }], A: { type: 'lift', x: 560, y: 570, exit: 1, back: 24 }, B: { type: 'bucket', x: 1090, y: 250 }, max: 330, hold: true },
      { t: 'duck', x: 420, y: 330 },
    ],
    solution: [{ t: 'moles_sack', x: 80, y: 190 }, { t: 'moles_sack', x: 1090, y: 150 }],
    fails: [[{ t: 'moles_sack', x: 80, y: 190 }, { t: 'moles_leaf', x: 1090, y: 150 }]],
  });
}
{ // 13 – Lift and hatch – the sack for the lift, the crystal for the hatch
  L.push({
    id: 'lift-and-hatch', par: 2, tools: { moles_sack: 1, moles_crystal: 1, moles_leaf: 1 }, learn: ['weight', 'pulley', 'chain_reaction'],
    statics: [exitLedge(220, 380, 1, 540, 3), J(890, 655, 220, 30), J(220, 775, 200, 40)],
    balls: [{ x: 240, y: 678, r: P }],
    goal: { type: 'bowl', x: 890, y: 640, w: 170 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 192, y: 90 }, { x: 98, y: 90 }], A: { type: 'lift', x: 220, y: 700, exit: 1 }, B: { type: 'bucket', x: 70, y: 300 }, max: 320 },
      { t: 'moles_hoist', wheels: [{ x: 648, y: 110 }, { x: 1032, y: 110 }], A: { type: 'gate', x: 620, y: 335, h: 120, m: 6 }, B: { type: 'bucket', x: 1060, y: 300 }, max: 140 },
      { t: 'duck', x: 1060, y: 560 },
    ],
    solution: [{ t: 'moles_sack', x: 70, y: 190 }, { t: 'moles_crystal', x: 1060, y: 190 }],
    fails: [[{ t: 'moles_crystal', x: 70, y: 190 }, { t: 'moles_sack', x: 1060, y: 190 }], [{ t: 'moles_sack', x: 70, y: 190 }]],
  });
}
{ // 14 – Mushroom hop – intro: pad (mushroom); hop over the wall and into the lift
  const s1 = J(120, 190, 200, 22, 10);
  L.push({
    id: 'mushroom-hop', par: 2, tools: { pad: 1, plank: 1, moles_sack: 1, moles_crystal: 1 }, learn: ['spring', 'pulley', 'weight'], intro: 'pad',
    statics: [s1, J(250, 775, 380, 30), KL(450, 650, 34, 300), exitLedge(620, 260, 1, 120, 5), J(880, 455, 220, 30)],
    balls: [on(s1, -70, P)],
    goal: { type: 'bowl', x: 880, y: 440, w: 170 },
    objs: [
      { t: 'moles_hoist', wheels: [{ x: 648, y: 70 }, { x: 1062, y: 70 }], A: { type: 'lift', x: 620, y: 600, exit: 1, back: 24 }, B: { type: 'bucket', x: 1090, y: 250 }, max: 340, hold: true },
      { t: 'duck', x: 330, y: 400 },
    ],
    solution: [{ t: 'pad', x: 360, y: 470, a: 15 }, { t: 'moles_sack', x: 1090, y: 150 }],
    fails: [[{ t: 'moles_sack', x: 1090, y: 150 }]],
  });
}
{ // 15 – The master mine – cart and lift; find the way yourself
  const s1 = J(150, 170, 240, 22, 8);
  L.push({
    id: 'master-mine', par: 3, tools: { plank: 2, pad: 1, moles_sack: 1, moles_crystal: 2, moles_leaf: 1 }, learn: ['pulley', 'weight', 'mechanism', 'chain_reaction'],
    statics: [s1, J(420, 780, 380, 30), exitLedge(780, 260, 1, 110, 5), J(980, 445, 200, 30)],
    balls: [on(s1, -90, P)],
    goal: { type: 'bowl', x: 980, y: 430, w: 160 },
    objs: [
      { t: 'moles_cart', x: 430, y: 340, tx: 640, ty: 400, tip: true },
      { t: 'moles_hoist', wheels: [{ x: 808, y: 70 }, { x: 1072, y: 70 }], A: { type: 'lift', x: 780, y: 620, exit: 1, back: 24 }, B: { type: 'bucket', x: 1100, y: 230 }, max: 360, hold: true },
      { t: 'duck', x: 560, y: 220 },
    ],
    solution: [{ t: 'plank', x: 330, y: 220, a: 30 }, { t: 'moles_crystal', x: 1085, y: 150 }, { t: 'moles_crystal', x: 1125, y: 150 }],
    fails: [[{ t: 'plank', x: 330, y: 220, a: 30 }, { t: 'moles_crystal', x: 1100, y: 130 }]],
  });
}

export const levels = defineLevels(L);
